import React from 'react';

import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Collapse, IconButton, Link as MuiLink, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import CaseClampedText from './CaseClampedText';
import CaseExpandedDetails from './CaseExpandedDetails';
import { MOCK_CASES } from './mockCases';
import { useExpandedCaseRows } from './useExpandedCaseRows';

// Widths sum to 100% so the table fits the page; long text clamps instead of scrolling.
// Short Detail, Customers Email and Case Owner appear only in the expanded row.
const EXPAND_COLUMN_WIDTH = '3%';
const COLUMNS = [
  { label: 'Case Number', width: '9%' },
  { label: 'Subject', width: '28%' },
  { label: 'Status', width: '16%' },
  { label: 'Date/Time Opened', width: '12%' },
  { label: 'Case Record Type', width: '13%' },
  { label: 'Contact Account Name', width: '10%' },
  { label: 'Zendesk Id', width: '9%' },
];
const TOTAL_COLUMNS = COLUMNS.length + 1;

const headerCellSx = { fontWeight: 'bold', color: 'primary.main', verticalAlign: 'bottom' } as const;

interface Props {
  buildCaseHref: (caseNumber: string) => string;
}

export default function CasesAllPage({ buildCaseHref }: Props) {
  const cases = MOCK_CASES.filter((item) => item.report === 'all');
  const { isExpanded, toggle, handleRowClick } = useExpandedCaseRows();

  return (
    <Table aria-label="All campaign operation cases" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <colgroup>
        <col style={{ width: EXPAND_COLUMN_WIDTH }} />
        {COLUMNS.map((column) => (
          <col key={column.label} style={{ width: column.width }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell />
          {COLUMNS.map((column) => (
            <TableCell key={column.label} sx={headerCellSx}>
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {cases.map((item) => {
          const isOpen = isExpanded(item.number);

          return (
            <React.Fragment key={item.number}>
              <TableRow
                hover
                onClick={(event) => handleRowClick(event, item.number)}
                sx={{ verticalAlign: 'top', cursor: 'pointer', '& > td': isOpen ? { borderBottom: 0 } : undefined }}
              >
                <TableCell padding="none" align="center" sx={{ verticalAlign: 'middle' }}>
                  <IconButton
                    size="small"
                    sx={{ p: 0.25 }}
                    aria-label={isOpen ? `Collapse case ${item.number}` : `Expand case ${item.number}`}
                    aria-expanded={isOpen}
                    onClick={() => toggle(item.number)}
                  >
                    {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <MuiLink component={Link} to={buildCaseHref(item.number)} underline="hover">
                    {item.number}
                  </MuiLink>
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.subject} maxLines={3}>
                    <MuiLink component={Link} to={buildCaseHref(item.number)} underline="hover">
                      {item.subject}
                    </MuiLink>
                  </CaseClampedText>
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.status} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.opened} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.recordType} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.contactAccount} />
                </TableCell>
                <TableCell>
                  <CaseClampedText text={item.zendeskId} maxLines={1} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={TOTAL_COLUMNS} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                  <Collapse in={isOpen} unmountOnExit>
                    <CaseExpandedDetails caseItem={item} caseHref={buildCaseHref(item.number)} />
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
